// @section 1 Mem-style global capture — ⌘⇧Space → classify → route preview

import { useEffect, useRef, useState } from 'react';

type Classification = { kind: string; icon: string; dest: string; tone: string };

function classify(t: string): Classification {
  const s = t.toLowerCase();
  if (/^(idea|maybe|what if|consider)/.test(s)) return { kind: 'strategy', icon: '💡', dest: 'Strategy · Brainstorm pile', tone: 'tone-purple' };
  if (/follow up|email|call|reach out|ping/.test(s)) return { kind: 'task', icon: '✓', dest: 'Tasks · auto-scheduled today', tone: 'tone-amber' };
  if (/move|reschedule|book|meeting/.test(s)) return { kind: 'calendar', icon: '📅', dest: 'Calendar · proposal', tone: 'tone-blue' };
  if (/[A-Z][a-z]+\s+[A-Z][a-z]+/.test(t) || /@/.test(s)) return { kind: 'contact_note', icon: '👤', dest: 'CRM · contact note', tone: 'tone-emerald' };
  if (/cash|invoice|expense|burn|runway/.test(s)) return { kind: 'erp_note', icon: '$', dest: 'ERP · note', tone: 'tone-emerald' };
  return { kind: 'note', icon: '📝', dest: 'Inbox · for later', tone: 'tone-muted' };
}

export function CaptureModal({
  open,
  onClose,
  onCommit,
}: {
  open: boolean;
  onClose: () => void;
  onCommit: (text: string, c: Classification) => void;
}) {
  const [text, setText] = useState('');
  const ref = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (open) { setText(''); setTimeout(() => ref.current?.focus(), 30); }
  }, [open]);

  if (!open) return null;
  const c = classify(text);
  const ready = text.trim().length > 0;

  return (
    <div className="cmdk-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="capture-modal">
        <div className="capture-head">
          <span>Capture</span>
          <span className="hint">⌘↵ commit · Esc cancel</span>
        </div>
        <textarea
          ref={ref}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'Enter' && ready) onCommit(text, c);
            else if (e.key === 'Escape') onClose();
          }}
          placeholder="Speak or type anything. OptiqHQ will classify and route it."
          className="capture-input"
          rows={5}
        />
        <div className="capture-foot">
          <span className="capture-foot-label">Helix preview</span>
          <span className={`capture-class ${ready ? c.tone : ''}`}>
            {ready ? `${c.icon} ${c.kind} → ${c.dest}` : 'Type a thought…'}
          </span>
          <button className="capture-commit" disabled={!ready} onClick={() => onCommit(text, c)}>
            Commit & route
          </button>
        </div>
      </div>
    </div>
  );
}
